/**
 * @fileoverview Waitlist CTA Overlay component
 * @active CURRENT ACTIVE IMPLEMENTATION
 * 
 * Provides a beautiful modal overlay for waitlist signup with gradient background
 * and modern UI
 * 
 * @see {@link src/components/ui/modals/DefaultModal.tsx} - Parent modal component
 * @see {@link src/app/api/trpc/app/user/router.server.ts} - Waitlist TRPC endpoints
 */

"use client";

import { useState, useEffect } from "react";
import { Box, Flex, Grid, Text, chakra, Input, Button } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import DefaultModal from "@/components/ui/modals/DefaultModal";
import { trpc } from "@/lib/trpc/client";
import LogoBig from "@/assets/svg/aventure_logo.svg";
import { setCookie } from "@/utils/authHelpers"; // Import setCookie

// Styled span for colored text
const Span = chakra("span", {});

// Subtle gradient shift animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Email validation schema
const emailSchema = object({
  email: string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

interface WaitlistFormValues {
  email: string;
}

interface WaitlistOverlayProps {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Optional company count to display */
  companyCount?: number;
  /** Determines the text content variant */
  variant?: 'default' | 'pageViewLimit';
}

/**
 * Modern, visually appealing waitlist signup overlay
 * Features gradient background and email capture form
 * 
 * @component
 * @param {WaitlistOverlayProps} props - Component props
 * @returns {JSX.Element} Modal overlay component
 */
const WaitlistOverlay = ({
  isOpen,
  onClose,
  companyCount: propCompanyCount = 0,
  variant = 'default', // Default to 'default' variant
}: WaitlistOverlayProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  
  // Fetch company count using tRPC
  const { 
    data: companyCountData,
    error: companyCountError,
    isLoading: isLoadingCount // Add isLoading state
  } = trpc.research.entity.getCount.useQuery(undefined, {
    // Only refetch on mount or when window regains focus
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: false,
    // Cache the result for 1 hour (3600 seconds)
    staleTime: 3600 * 1000,
    // Don't retry on error to avoid excessive requests
    retry: false
  });
  
  // Mutation for adding to waitlist
  const { mutate: addToWaitlist } = trpc.app.user.addToWaitlist.useMutation({
    onSuccess: (data) => {
      setIsSuccess(true);
      setIsError(false);
      
      // Set simple success message without redundant API message
      const successMessage = data.alreadyOnWaitlist 
        ? 'Already on the list! ✨' 
        : 'You\'re on the list! ✨';
      
      setResponseMessage(successMessage);
      reset();
      setIsSubmitting(false);

        // Set flags to prevent showing the page limit overlay again
        try {
          localStorage.setItem('waitlistSignedUp', 'true');
          sessionStorage.setItem('waitlistSignedUp', 'true');
          setCookie('waitlistSignedUp', 'true'); // Set the cookie as well
        } catch (e) {
          console.error("Error setting signup flags in storage:", e);
        }
      },
      onError: (error) => {
      setIsSuccess(false);
      setIsError(true);
      setResponseMessage(error.message || "😢 Ah nuts, we couldn't add you to the waitlist. Please try again.");
      setIsSubmitting(false);
    }
  });
  
  // Log errors but don't break the UI
  // Show the fallback thousands of companies in UI if there's an error
  useEffect(() => {
    if (companyCountError) {
      console.error('Error fetching company count:', companyCountError);
    }
  }, [companyCountError]);
  
  // Use the fetched count or fall back to the prop value
  const companyCount = companyCountData?.count ?? propCompanyCount;
 
   // Format company count for display, handling loading state
   const companyMessage = isLoadingCount 
     ? "Loading company data..." // Show loading text
     : companyCount
       ? `${new Intl.NumberFormat("en-US").format(companyCount)}+ funded companies`
       : "thousands of funded companies"; // Fallback if count is 0 or fetch failed
 
   const {
     register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<WaitlistFormValues>({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  // Reset success state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsSuccess(false);
      setIsError(false);
      setResponseMessage(null);
    }
  }, [isOpen]);

  const onSubmitHandler = async (data: WaitlistFormValues) => {
    setIsSubmitting(true);
    setIsError(false);
    
    // Call the tRPC mutation directly - it will handle checking if user already exists
    addToWaitlist({ email: data.email });
   };
 
   return (
     <DefaultModal
      content_style={{ p: 0, overflow: "hidden" }}
      isOpen={isOpen}
      onClose={onClose}
      overlayStyle={{ 
        backdropFilter: "blur(12px) saturate(180%)",
        bg: "rgba(30, 30, 40, 0.65)",
        transition: "all 0.3s ease-in-out",
      }}
      modalStyle={{
        boxShadow: "0 25px 70px -12px rgba(0, 0, 0, 0.35)",
        overflow: "hidden",
        maxW: { base: "90%", md: "700px", lg: "850px" },
        border: "1px solid rgba(255,255,255,0.09)",
        borderRadius: "12px",
        transition: "transform 0.3s ease-out",
        transform: isOpen ? "translateY(0)" : "translateY(20px)",
      }}
    >
      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        templateRows={{ base: "auto auto", md: "1fr" }}
        overflow="hidden"
      >
        {/* Form Section */}
        <Box
          p={{ base: "28px", md: "36px" }} 
          bg="white"
          transition="all 0.3s ease"
        >
          <Box mb="28px">
            <Text
              fontWeight="700"
              fontSize={{ base: "32px", md: "36px" }}
              lineHeight="1.2"
              mb="16px"
              transition="opacity 0.3s ease, transform 0.3s ease"
              sx={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? "translateY(0)" : "translateY(10px)",
              }}
            >
              {variant === 'pageViewLimit' ? (
                <>Wow, you're diving deep into aVenture! <Span color="#D4470F">🎉</Span></>
              ) : (
                <>Your free, early access <Span color="#D4470F">awaits</Span>.</>
              )}
            </Text>
            
            <Text
              fontSize="16px"
              lineHeight="1.5"
              color="#4A5568"
              transition="opacity 0.4s ease, transform 0.4s ease"
              sx={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? "translateY(0)" : "translateY(10px)",
                transitionDelay: "0.05s"
              }}
            >
              {variant === 'pageViewLimit' 
                ? "We're thrilled to see you exploring so much! To keep discovering valuable company insights, please join our free waitlist with just your email. No extra steps needed!"
                : "We're opening access to the aVenture platform to waitlist members first. Coming May, 2025."
              }
            </Text>
          </Box>

          <Box 
            as="form" 
            onSubmit={handleSubmit(onSubmitHandler)} 
            width="100%"
            transition="opacity 0.5s ease, transform 0.5s ease"
            sx={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? "translateY(0)" : "translateY(10px)",
              transitionDelay: "0.1s"
            }}
          >
            <Box position="relative" width="100%" mb="20px">
              <Text
                as="label"
                htmlFor="waitlist-email"
                fontSize="14px"
                fontWeight="500"
                mb="4px"
                display="block"
              >
                Email address
              </Text>
              <Input
                id="waitlist-email"
                h={{ base: "46px", md: "50px" }}
                px={{ base: "16px", md: "18px" }}
                width="100%"
                borderRadius="4px"
                borderColor={errors.email ? "errorRed" : "#CCD2E3"}
                borderWidth="1px"
                fontSize={{ base: "16px", md: "16px" }}
                bg="white"
                placeholder="Enter your email"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "#A0AEC0" }}
                _focus={{ 
                  borderColor: "#134E88",
                  boxShadow: "0 0 0 1px #134E88" 
                }}
                transition="all 0.2s ease"
                {...register("email")}
                isDisabled={isSubmitting || isSuccess}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <Text 
                  id="email-error"
                  position="absolute" 
                  bottom="-20px" 
                  left="0" 
                  color="errorRed" 
                  fontSize="12px"
                  fontWeight="500"
                  role="alert"
                >
                  {errors.email.message}
                </Text>
              )}
            </Box>

            <Box mb={(isSuccess || isError) ? "16px" : "0"}>
              <Button
                type="submit"
                width="100%"
                height={{ base: "46px", md: "50px" }}
                px={{ base: "24px", md: "32px" }}
                borderRadius="4px"
                fontWeight="600"
                fontSize={{ base: "16px", md: "16px" }}
                transition="all 0.2s ease-in-out"
                position="relative"
                overflow="hidden"
                bg={isSubmitting ? "#BDBDBD" : isSuccess ? "#29AE61" : isError ? "#E53E3E" : "#D4470F"}
                color="white"
                cursor={isSubmitting ? "not-allowed" : "pointer"}
                _hover={{ 
                  bg: isSubmitting ? "#BDBDBD" : isSuccess ? "#219653" : isError ? "#C53030" : "#BF380A" 
                }}
                isDisabled={isSubmitting || isSuccess}
              >
                <Box
                  as="span"
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  opacity="0"
                  bg="linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)"
                  sx={{
                    transition: "opacity 0.3s ease, transform 0.4s ease",
                    transform: "translateX(-100%)",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateX(100%)",
                    }
                  }}
                />
                {isSubmitting ? "Joining..." : isSuccess ? "Joined!" : isError ? "Try Again" : "Join Waitlist →"}
              </Button>
            </Box>

            {(isSuccess || isError) && (
              <Text 
                color={isSuccess ? "green.500" : "red.500"}
                fontSize="14px" 
                fontWeight="600" 
                mt="16px"
                textAlign="center"
                transition="opacity 0.3s ease"
                sx={{ opacity: (isSuccess || isError) ? 1 : 0 }}
              >
                {responseMessage || (isSuccess 
                  ? "You've been added to our waitlist! ✨ We'll notify you soon when your slot is available."
                  : "Something went wrong. Please try again.")}
              </Text>
            )}
          </Box>
        </Box>

        {/* Gradient Background Section */}
        <Box
          position="relative"
          overflow="hidden"
          minH={{ base: "300px", md: "auto" }}
        >
          {/* Enhanced Gradient Background */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            background="linear-gradient(135deg, #9066E3, #E366B5, #66C1E3)"
            backgroundSize="200% 200%"
            zIndex="0"
            sx={{
              animation: `${gradientShift} 15s ease infinite`,
            }}
            _after={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
              zIndex: 1
            }}
          />
          
          {/* Content */}
          <Flex 
            flexDirection="column"
            justify="space-between" 
            height="100%" 
            zIndex="1" 
            position="relative" 
            p={{ base: "30px", md: "36px" }}
          >
            <Box>
              <Text 
                fontWeight="700"
                fontSize={{ base: "32px", md: "40px" }}
                lineHeight="1.2"
                color="white"
                mb="6px"
                textShadow="0 2px 10px rgba(0,0,0,0.1)"
                transition="opacity 0.3s ease, transform 0.3s ease"
                sx={{
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(10px)",
                }}
              >
                Unlock data on
              </Text>
              <Text 
                fontWeight="700"
                fontSize={{ base: "32px", md: "40px" }}
                lineHeight="1.2"
                color="white"
                mb="24px"
                textShadow="0 2px 10px rgba(0,0,0,0.1)"
                transition="opacity 0.3s ease, transform 0.3s ease"
                sx={{
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(10px)",
                  transitionDelay: "0.05s"
                }}
              >
                {companyMessage}
              </Text>
              
              <Text 
                fontSize="16px" 
                lineHeight="1.5"
                color="white"
                maxW="400px"
                transition="opacity 0.3s ease, transform 0.3s ease"
                sx={{
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(10px)",
                  transitionDelay: "0.1s"
                }}
              >
                aVenture provides research on private startups funded by venture
                capital, private equity, angel investors, family offices, and
                more.
              </Text>
            </Box>

            <Box 
              mt="auto"
              transition="opacity 0.3s ease, transform 0.3s ease"
              sx={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? "translateY(0)" : "translateY(10px)",
                transitionDelay: "0.15s"
              }}
            >
              <LogoBig width={180} height={38} />
            </Box>
          </Flex>
        </Box>
      </Grid>
    </DefaultModal>
  );
};

export default WaitlistOverlay;
